import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@nesso/ui/primitives/form';
import { Input } from '@nesso/ui/primitives/input';

import { npiSchema } from '@/entities/practice-member';
import { SettingsRow } from '@/shared/ui/settings/settings-row';

type Props = {
    value: string | null;
    onSave: (value: string | null) => void;
    isDisabled?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
};

type FormValues = { value: string };

// Blank is allowed here (clearing is valid) — `npiSchema` itself requires exactly ten digits, so
// an empty draft is checked against it only when it is not blank.
const npiFormSchema = z.object({
    value: z.string().refine((raw) => raw.trim() === '' || npiSchema.safeParse(raw).success, {
        message: 'Enter a 10-digit NPI',
    }),
});

const NPI_REGISTRY_URL = 'https://npiregistry.cms.hhs.gov/search';

export function ProfileNpiRow({ value, onSave, isDisabled, onEditingChange }: Props) {
    const [isEditing, setIsEditing] = useState(false);

    const form = useForm<FormValues>({
        resolver: zodResolver(npiFormSchema),
        defaultValues: { value: value ?? '' },
    });

    const startEditing = () => {
        setIsEditing(true);
        onEditingChange?.(true);
    };

    const stopEditing = () => {
        setIsEditing(false);
        onEditingChange?.(false);
    };

    const handleCancel = () => {
        form.reset({ value: value ?? '' });
        stopEditing();
    };

    // Submitting always sends `npi` explicitly — the trimmed value, or `null` to clear — so the
    // command's "absent means leave alone, null means clear" contract is respected: a save from
    // this row is never the omitted-key case, that only happens when a different row saves.
    const handleSubmit = (data: FormValues) => {
        const trimmed = data.value.trim();
        onSave(trimmed === '' ? null : trimmed);
        stopEditing();
    };

    if (isEditing) {
        return (
            <Form {...form}>
                <SettingsRow
                    label="NPI"
                    caption={
                        <>
                            Need help finding your NPI? You can look it up{' '}
                            <a
                                href={NPI_REGISTRY_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="underline"
                            >
                                here
                            </a>
                        </>
                    }
                    action={{ label: 'Cancel', onClick: handleCancel }}
                    className="gap-6"
                >
                    <FormField
                        control={form.control}
                        name="value"
                        render={({ field }) => (
                            <FormItem className="w-40 gap-3">
                                <FormControl>
                                    <Input placeholder="NPI" aria-label="NPI" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div>
                        <Button
                            type="button"
                            size="lg"
                            onClick={form.handleSubmit(handleSubmit)}
                            disabled={form.formState.isSubmitting}
                        >
                            Save
                        </Button>
                    </div>
                </SettingsRow>
            </Form>
        );
    }

    const actionLabel = value ? 'Edit' : 'Add';

    return (
        <SettingsRow
            label="NPI"
            caption={value ?? 'Not provided'}
            action={{
                label: actionLabel,
                onClick: startEditing,
                disabled: isDisabled,
                ariaLabel: `${actionLabel} NPI`,
            }}
            className={cn('gap-6', isDisabled && 'opacity-50')}
        />
    );
}
